/* eslint-disable @typescript-eslint/no-magic-numbers */
import { Injectable } from '@angular/core';
import * as d3 from 'd3';
import { DateTime } from 'luxon';

import { SteppedLine, steppedLine } from '@shared/helpers/graph/stepped-line.helper';
import { D3ChartSvg, D3ChartSvgg, D3XScale, D3YScale } from '@shared/types/common-graph.types';
import { isDefined } from '@shared/utils/is-defined';
import { getRuntimeUniqueId } from '@shared/utils/runtime-unique-id';

import { ChartLineConfig, ChartLineType, ChartXAxisConfig } from '../chart.view-model';

interface LineChartData {
  x: number;
  y: number;
}

@Injectable()
export class LineChartService<Data extends object> {
  public drawGradientArea(
    svg: D3ChartSvg,
    data: Data[],
    lineChartAreaMaxWidth: number,
    linesContainer: D3ChartSvgg,
    xScale: D3XScale,
    yScale: D3YScale,
    config: ChartLineConfig<Data>,
    xAxisConfig: ChartXAxisConfig<Data>
  ): void {
    const gradientId = `${getRuntimeUniqueId()}`;
    const clipPathId = `${getRuntimeUniqueId()}`;

    this.createGradient(svg, gradientId, config.gradientConfig!.gradientTextColorClass);
    this.drawClipPath(data, lineChartAreaMaxWidth, svg, clipPathId, xScale, yScale, config, xAxisConfig);

    const width = xScale.range()[1];
    const height = yScale.range()[0];

    linesContainer
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', width)
      .attr('height', height)
      .attr('style', `fill: url(#${gradientId})`)
      .attr('clip-path', `url(#${clipPathId})`);
  }

  private createGradient(svg: D3ChartSvg, gradientId: string, textColorClass: string): void {
    const defs = svg.select('defs');
    const gradient = defs.append('linearGradient').attr('id', gradientId).attr('x1', 0).attr('x2', 0).attr('y1', 0).attr('y2', 1);

    gradient.append('stop').attr('offset', '0%').attr('class', textColorClass).attr('stop-color', 'currentColor').attr('stop-opacity', 0.9);

    gradient
      .append('stop')
      .attr('offset', '50%')
      .attr('class', textColorClass)
      .attr('stop-color', 'currentColor')
      .attr('stop-opacity', 0.6);

    gradient.append('stop').attr('offset', '100%').attr('class', textColorClass).attr('stop-color', 'currentColor').attr('stop-opacity', 0);
  }

  private drawClipPath(
    data: Data[],
    lineChartAreaMaxWidth: number,
    svg: D3ChartSvg,
    clipPathId: string,
    xScale: D3XScale,
    yScale: D3YScale,
    config: ChartLineConfig<Data>,
    xAxisConfig: ChartXAxisConfig<Data>
  ): void {
    const defs = svg.select('defs');
    const clipPath = defs.append('clipPath').attr('id', clipPathId);

    const dAttrFn =
      config.lineType === ChartLineType.Stepped
        ? this.getSteppedLineChartPathFn(xScale, yScale, lineChartAreaMaxWidth, -2)
        : this.getLinearLineChartPathFn(xScale, yScale, -2);

    let continuousDataSets = this.getContinuousDataSets(
      data.map((d, idx): LineChartData => {
        const mtu = d[xAxisConfig.dataKey] as DateTime | number;
        const mtuMillis = typeof mtu === 'number' ? mtu : mtu.toMillis();

        return {
          x: mtuMillis,
          y: isDefined(config.dataKey) ? (d[config.dataKey] as number) : (config.dataMapper!(d, idx) as number),
        };
      })
    );

    if (config.spannedAcrossXBandwidth) {
      continuousDataSets = continuousDataSets.map((dataSet) => {
        const bandwidthMillis = this.getBandwidthMillisOfData(dataSet);

        return this.spanDataAcrossBandwidth(dataSet, bandwidthMillis);
      });
    }

    continuousDataSets.forEach((dataSet) => {
      const clipPathPath = clipPath.append('path');

      clipPathPath.datum(dataSet).attr('fill', 'none').attr('stroke-linejoin', 'round').attr('d', dAttrFn);

      const clipPathD = clipPathPath.attr('d').replace('M', 'L');

      const leftPoint = xScale(dataSet[0].x);
      const bottomPoint = yScale.range()[0];

      const rightPoint = xScale(dataSet[dataSet.length - 1].x);

      const fixedPathD = `M${leftPoint},${bottomPoint}${clipPathD}L${rightPoint},${bottomPoint}Z`;
      clipPathPath.attr('d', fixedPathD);
    });
  }

  public drawLineChart(
    data: Data[],
    linesContainer: D3ChartSvgg,
    lineChartAreaMaxWidth: number,
    xScale: D3XScale,
    yScale: D3YScale,
    config: ChartLineConfig<Data>,
    xAxisConfig: ChartXAxisConfig<Data>
  ): void {
    this.drawLineForAsset(
      data.map((d, idx): LineChartData => {
        const mtu = d[xAxisConfig.dataKey] as DateTime | number;
        const mtuMillis = typeof mtu === 'number' ? mtu : mtu.toMillis();

        return {
          x: mtuMillis,
          y: isDefined(config.dataKey) ? (d[config.dataKey] as number) : (config.dataMapper!(d, idx) as number),
        };
      }),
      linesContainer,
      lineChartAreaMaxWidth,
      xScale,
      yScale,
      config
    );
  }

  private drawLineForAsset(
    data: LineChartData[],
    linesContainer: D3ChartSvgg,
    lineChartAreaMaxWidth: number,
    xScale: D3XScale,
    yScale: D3YScale,
    config: ChartLineConfig<Data>
  ): void {
    this.drawLine(data, linesContainer, lineChartAreaMaxWidth, xScale, yScale, config);
  }

  private drawLine(
    data: LineChartData[],
    linesContainer: D3ChartSvgg,
    lineChartAreaMaxWidth: number,
    xScale: D3XScale,
    yScale: D3YScale,
    config: ChartLineConfig<Data>
  ): void {
    const dAttrFn =
      config.lineType === ChartLineType.Stepped
        ? this.getSteppedLineChartPathFn(xScale, yScale, lineChartAreaMaxWidth)
        : this.getLinearLineChartPathFn(xScale, yScale);

    let continuousDataSets = this.getContinuousDataSets(data);

    if (config.spannedAcrossXBandwidth) {
      continuousDataSets = continuousDataSets.map((dataSet) => {
        const bandwidthMillis = this.getBandwidthMillisOfData(dataSet);

        return this.spanDataAcrossBandwidth(dataSet, bandwidthMillis);
      });
    }

    continuousDataSets.forEach((dataSet) => {
      linesContainer
        .append('path')
        .datum(dataSet)
        .attr('fill', 'none')
        .attr('class', config.strokeColorClass)
        .attr('stroke-width', config.strokeWidth ?? 2)
        .attr('stroke-linejoin', 'round')
        .attr('d', dAttrFn);
    });
  }

  private getContinuousDataSets(data: LineChartData[]): LineChartData[][] {
    const continuousDataSets: LineChartData[][] = [];

    let currentSet: LineChartData[] = [];
    data.forEach((datum) => {
      if (!isDefined(datum.y)) {
        if (currentSet.length > 0) {
          continuousDataSets.push(currentSet);
          currentSet = [];
        }

        return;
      }

      currentSet.push(datum);
    });

    if (currentSet.length > 0) {
      continuousDataSets.push(currentSet);
    }

    return continuousDataSets;
  }

  private getLinearLineChartPathFn(xScale: D3XScale, yScale: D3YScale, yOffset = 0) {
    return d3
      .line<LineChartData>()
      .x((d): number => {
        return xScale(d.x);
      })
      .y((d): number => {
        return yScale(d.y) + yOffset;
      });
  }

  private getSteppedLineChartPathFn(
    xScale: D3XScale,
    yScale: D3YScale,
    lineChartAreaMaxWidth: number,
    yOffset = 0
  ): SteppedLine<LineChartData> {
    return steppedLine<LineChartData>(
      (d): number => {
        return xScale(d.x);
      },
      (d): number => {
        return yScale(d.y) + yOffset;
      },
      lineChartAreaMaxWidth
    );
  }

  private getBandwidthMillisOfData(data: LineChartData[]): number {
    return data[0] && data[1] ? data[1].x - data[0].x : 0;
  }

  private spanDataAcrossBandwidth(data: LineChartData[], bandwidthMillis: number): LineChartData[] {
    const newData = data.map((d): LineChartData => {
      return {
        x: d.x - bandwidthMillis / 2,
        y: d.y,
      };
    });

    if (newData.length > 0) {
      newData.push({
        x: newData[newData.length - 1].x + bandwidthMillis,
        y: newData[newData.length - 1].y,
      });
    }

    return newData;
  }
}
